import React, { useEffect } from "react";
import { DialogContainer } from "react-md";
import AddressForm from "./AddressForm";
import { mapLocationToForm, buildPayload } from "./addressMapper";

import CommonService from "../../services/CommonService";
import EventService from "service/event/EventService";
// import NavigoDashboardUtils from "../../../../misc/modules/navigo-module/services/NavigoDashboardUtils";
import NavigoDashboardUtils from "../../../../../../../misc/modules/navigo-module/services/NavigoDashboardUtils";

const commonService = new CommonService();
const navigoDashboardUtils = new NavigoDashboardUtils();

/**
 * coordinates → address object
 */
function getAddressByLatLng({ lat, lng }) {
  if (lat == null || lng == null) {
    return Promise.reject("lat/lng required");
  }

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getAddressByCoordinates(
      { latitude: lat, longitude: lng },
      (res) => resolve(res),
      (err) => reject(err),
    );
  });
}

export default function AddressFlowDialog({
  flowState,
  setFlowState,
  closeFlow,
  openLocationDialog, // pass this from parent
  patientId,
}) {
  const { visible, step, locationData, formData, editingAddressId } = flowState;

  // 🔹 STEP 1 → MAP
  useEffect(() => {
    if (visible && step === "map") {
      (async () => {
        const result = await openLocationDialog(locationData);

        if (!result?.confirmed) {
          closeFlow({ confirmed: false });
          return;
        }

        const mapped = mapLocationToForm(result);

        setFlowState((prev) => ({
          ...prev,
          locationData: result,
          formData: {
            ...prev.formData,
            ...mapped,
            coordinates: {
              latitude: result.latLng.lat,
              longitude: result.latLng.lng,
            },
          },
          step: "form",
        }));
      })();
    }
  }, [visible, step]);

  // 🔹 FETCH ADDRESS FROM COORDINATES
  useEffect(() => {
    if (visible && step === "form" && formData?.coordinates) {
      const { latitude, longitude } = formData.coordinates;
      if (latitude && longitude && latitude !== 0 && longitude !== 0) {
        (async () => {
          try {
            const address = await getAddressByLatLng({
              lat: latitude,
              lng: longitude,
            });
            setFlowState((prev) => ({
              ...prev,
              googleAddress: {
                ...prev.formData,
                line1: address.building || address.housing?.split(",")[0] || "",
                line2:
                  address.street ||
                  `${address.neighborhood || ""} ${address.subLocality || ""}`.trim() ||
                  "",
                city: address.city || "",
                state: address.state || "",
                pincode: address.pincode || "",
                landmark: address.landmark || "",
              },
            }));
          } catch (e) {
            console.error("Failed to fetch address:", e);
          }
        })();
      }
    }
  }, [visible, step, formData?.coordinates]);

  // 🔹 CHANGE LOCATION
  const handleChangeLocation = async () => {
    // STEP 1: hide form dialog
    setFlowState((prev) => ({
      ...prev,
      step: "map",
    }));

    // STEP 2: open map
    const result = await openLocationDialog(locationData);

    if (!result?.confirmed) {
      // user cancelled → go back to form
      setFlowState((prev) => ({
        ...prev,
        step: "form",
      }));
      return;
    }

    const mapped = mapLocationToForm(result);

    // STEP 3: back to form with updated location
    setFlowState((prev) => ({
      ...prev,
      locationData: result,
      formData: {
        ...prev.formData,
        ...mapped,
        coordinates: {
          latitude: result.latLng.lat,
          longitude: result.latLng.lng,
        },
      },
      step: "form",
    }));
  };

  // 🔹 SAVE
  const handleSubmit = () => {
    // Validate required fields
    const requiredFields = [
      { key: "name", label: "Full name" },
      { key: "phoneNo", label: "Mobile number" },
      { key: "line1", label: "House No. / Floor / Building" },
      { key: "line2", label: "Colony / Street / Area" },
      { key: "pincode", label: "Pincode" },
      { key: "city", label: "City" },
      { key: "district", label: "District" },
      { key: "state", label: "State" },
      { key: "type", label: "Address type" },
    ];

    for (const field of requiredFields) {
      if (!formData?.[field.key]?.toString().trim()) {
        EventService.emit("showError", {
          message: `${field.label} is required`,
        });
        return;
      }
    }

    // Validate coordinates (geo location must be selected)
    if (
      !formData?.coordinates?.latitude ||
      !formData?.coordinates?.longitude ||
      (formData.coordinates.latitude === 0 &&
        formData.coordinates.longitude === 0)
    ) {
      EventService.emit("showError", {
        message: "Please select a geo location using the Change button",
      });
      return;
    }

    // Validate mobile number — must be exactly 10 digits
    if (!/^\d{10}$/.test(formData.phoneNo?.toString().trim())) {
      EventService.emit("showError", {
        message: "Mobile number must be exactly 10 digits",
      });
      return;
    }

    // Validate pincode — must be exactly 6 digits
    if (!/^\d{6}$/.test(formData.pincode?.toString().trim())) {
      EventService.emit("showError", {
        message: "Pincode must be exactly 6 digits",
      });
      return;
    }

    const payload = buildPayload(formData, locationData, patientId);

    const onSuccess = (data) => {
      closeFlow({ confirmed: true, data });
    };

    const onError = (err) => {
      EventService.emit("showError", {
        message: err?.message || "Something went wrong",
      });
    };

    if (editingAddressId) {
      commonService.updatePrimaryAddress(
        patientId,
        editingAddressId,
        payload,
        onSuccess,
        onError,
      );
    } else {
      commonService.addPrimaryAddress(patientId, payload, onSuccess, onError);
    }
  };

  if (!visible || step !== "form") return null;

  return (
    <DialogContainer
      id="address-form-dialog"
      visible={visible && step === "form"}
      onHide={() => closeFlow({ confirmed: false })}
      dialogStyle={{ padding: 16, width: 900 }}
    >
      <AddressForm
        data={formData || {}}
        googleAddress={flowState.googleAddress}
        onChange={(data) =>
          setFlowState((prev) => ({ ...prev, formData: data }))
        }
        onSubmit={handleSubmit}
        onChangeLocation={handleChangeLocation}
        onHide={() => closeFlow({ confirmed: false })}
      />
    </DialogContainer>
  );
}
